import {$s, $t} from '@bitstillery/common/app'
import {DateTime} from 'luxon'

/**
 * Memoized `Intl` formatters, keyed by locale + options.
 *
 * Constructing an `Intl.NumberFormat`/`DateTimeFormat` is far more expensive than calling `.format()` on
 * one — it resolves the locale, which is why i18next-style locale plumbing shows up hot in a profile. The
 * formatters here are pure functions of (locale, options), so they can be built once and reused.
 *
 * This is not a micro-optimisation: these helpers are called from `view()` bodies, so every one of them
 * ran again on every redraw. A profile of 60 no-op redraws of the portal landing page
 * (`bun cli profile portal / --scenario redraw`) attributed ~30ms to `format_money_responsive` alone,
 * essentially all of it construction.
 *
 * The cache is unbounded, which is safe because the key space is not user-controlled: it is the handful
 * of (locale, options) pairs that appear literally in the call sites below.
 */
const number_formatters = new Map<string, Intl.NumberFormat>()
const date_formatters = new Map<string, Intl.DateTimeFormat>()

function number_format(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
    const key = `${locale}|${JSON.stringify(options)}`
    let formatter = number_formatters.get(key)
    if (!formatter) {
        formatter = new Intl.NumberFormat(locale, options)
        number_formatters.set(key, formatter)
    }
    return formatter
}

function date_format(locale: string, options: Intl.DateTimeFormatOptions = {}): Intl.DateTimeFormat {
    const key = `${locale}|${JSON.stringify(options)}`
    let formatter = date_formatters.get(key)
    if (!formatter) {
        formatter = new Intl.DateTimeFormat(locale, options)
        date_formatters.set(key, formatter)
    }
    return formatter
}

export const CURRENCY_SYMBOLS: Record<string, string> = {
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    USD: '$',
}

export const FILE_TYPE_ICONS = {
    docx: 'document_outline',
    gif: 'image',
    jpeg: 'image',
    jpg: 'image',
    pdf: 'pdf',
    png: 'image',
    svg: 'image',
    tiff: 'image',
}

export function days_left(date: string): number {
    const today = new Date()
    const time_diff = new Date(date).getTime() - today.getTime()
    return Math.ceil(time_diff / (1000 * 3600 * 24))
}

/**
 * Formats a float to a certain number of fixed fraction digits, formatting according to users locale.
 *
 * @param float_to_display
 * @param number_of_fraction_digits The number of fraction digits.
 */
export function displayable_float(
    float_to_display: string | number | null | undefined,
    number_of_fraction_digits: number = 2,
    locale = 'en',
): string {
    const formatter = number_format(locale, {
        maximumFractionDigits: number_of_fraction_digits,
        minimumFractionDigits: number_of_fraction_digits,
        style: 'decimal',
    })
    if (!float_to_display) {
        return formatter.format(0)
    }
    return formatter.format(Number(float_to_display))
}

/**
 * Displays the integer, taking into account if it is 0, null or undefined.
 *
 * @param to_display
 */
export function displayable_integer(to_display: number | string | null | undefined): string {
    return to_display?.toString() ?? '0'
}

/**
 * Format bytes as a Mb/Gb string.
 *
 * @param bytes The bytes to format.
 * @param decimals The number of decimals to display, default 2.
 */
export function format_bytes(bytes: string | number | null | undefined, decimals = 2): string {
    if (!bytes || !Number(bytes)) {
        return '0 Bytes'
    }
    const bytes_as_num = Number(bytes)

    const k = 1024
    const dm = Math.max(0, decimals)
    const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']

    const i = Math.floor(Math.log(bytes_as_num) / Math.log(k))

    return `${Number.parseFloat((bytes_as_num / k ** i).toFixed(dm))} ${sizes[i] ?? ''}`
}

export function format_month_day(date: string | Date) {
    if (!date) {
        return ''
    }

    const date_obj = date instanceof Date ? date : new Date(date)
    return date_obj.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'long',
    })
}

export function format_month_day_year(date: string | Date) {
    if (!date) {
        return ''
    }

    const date_obj = date instanceof Date ? date : new Date(date)
    return date_obj.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    })
}

export function format_days_since_date(date: string | number | Date) {
    const today = new Date()
    const entry = new Date(date)
    const diff = Math.abs(entry.getTime() - today.getTime())
    const days = Math.floor(diff / (1000 * 3600 * 24))
    if (days === 0) {
        return 'Today'
    } else if (days === 1) {
        return '1 day ago'
    }
    return `${days} days ago`
}

export function format_delivery_period(value: number | null | undefined) {
    // Even if less then 1 always display as 1 week
    if (!value || value <= 1) {
        return $t('offer.delivery_week', {count: 1})
    }

    return $t('offer.delivery_week', {count: value})
}

export function format_leading_zero(input: number) {
    const input_str = `${input}`

    if (input_str.length < 2) {
        return `0${input_str}`
    }

    return input_str
}

/**
 * Formats a number as money according to the users locale.
 *
 * @param money The amount to format.
 * @param currency In which currency to format ("EUR", "GBP", etc).
 * @param locale The locale to use, default = nl
 *
 * @returns A as currency formatted string, or "" if the argument money is undefined or null.
 */
export function format_money_with_symbol(
    money: number | string | null | undefined,
    currency: string | undefined | null,
    locale = 'nl',
) {
    if (currency && (money || money === 0)) {
        const formatter = number_format(locale, {
            currency: currency,
            style: 'currency',
        })
        if (money || money === 0) {
            return formatter.format(Number(money))
        }
    }
    return ''
}

/**
 * Format the date as a nl-date string (dd-mm-yyyy).
 *
 * @param date The date to format.
 * @param locale The locale override to use. If not set nl-NL is used.
 */
export const format_date = function format_date(date: Date | null | undefined, locale: string = 'nl-NL'): string {
    if (!date) {
        return ''
    }
    return date_format(locale).format(date)
}

/**
 * Format the ISO date_time as a nl-date string (dd-mm-yyyy).
 *
 * @param date_time The date to format.
 * @param locale The locale override to use. If not set nl-NL is used.
 */
export const format_iso_to_date = function format_iso_to_date(
    date_time: string | null | undefined,
    locale: string = 'nl-NL',
): string {
    if (!date_time) {
        return ''
    }
    return format_date(new Date(date_time), locale)
}

/**
 * Format the ISO date_time as a nl-date string (dd-mm-yyyy hh:mm:ss).
 *
 * @param date_time The date to format.
 * @param locale The locale override to use. If not set nl-NL is used.
 */
export const format_iso_to_date_time = function format_iso_to_date_time(
    date_time: string | null | undefined,
    locale = 'nl-NL',
): string {
    if (!date_time) {
        return ''
    }
    return date_format(locale, {
        dateStyle: 'short',
        timeStyle: 'medium',
    }).format(new Date(date_time))
}

/**
 * Format the ISO date_time as a fixed string (yyyy-MM-dd).
 *
 * This function retains leading zeroes, as opposed to format_iso_to_date. This keeps it consistent
 * with already existing date strings that were formatted via format-date from utils.ls.
 *
 * @param date_time The date to format.
 *
 * @return the formatted date string.
 */
export const format_iso_to_fixed_date_format = function format_iso_to_fixed_date_format(date_time: string): string {
    if (!date_time) {
        return ''
    }
    const as_dt = new Date(date_time)
    const month = String(as_dt.getMonth() + 1).padStart(2, '0')
    const day_of_month = String(as_dt.getDate()).padStart(2, '0')
    return `${as_dt.getFullYear()}-${month}-${day_of_month}`
}

/**
 * Return the date_time in a relative format (yesterday, today, 8 days ago, etc).
 *
 * @see https://moment.github.io/luxon/docs/class/src/datetime.js~DateTime.html#instance-method-toRelativeCalendar
 * @param date_time The iso datetime format to make relative.
 */
export const format_iso_to_relative = function format_iso_to_relative(date_time: string): string {
    if (date_time === '') {
        return ''
    }
    const as_dt = DateTime.fromISO(date_time)
    return as_dt.toRelativeCalendar({locale: 'en'}) || format_iso_to_date_time(date_time)
}

/**
 * Return the date_time in a relative format (yesterday, today, 8 days ago, etc) and include the time (e.g., 'Today at 14:30').
 *
 * @param date_time The iso datetime format to make relative.
 * @param locale The locale to use for the relative part (default 'en').
 * @param timeFormat The time format string for Luxon (default 'HH:mm').
 * @param tz The timezone to use for the date_time (default 'local').
 */
export const format_iso_to_relative_with_time = function format_iso_to_relative_with_time(
    date_time: string | null | undefined,
    locale: string = 'en',
    timeFormat: string = 'HH:mm',
    tz: string = 'local',
): string {
    if (!date_time) {
        return ''
    }
    const as_dt = DateTime.fromISO(date_time, {zone: tz})
    const now = DateTime.now().setZone(tz)
    const diff_days = Math.abs(Math.floor(as_dt.startOf('day').diff(now.startOf('day'), 'days').days))

    let date_part: string
    if (diff_days <= 2) {
        date_part = as_dt.toRelativeCalendar({locale}) || format_iso_to_date_time(date_time)
    } else {
        date_part = as_dt.toFormat('ccc dd-MM-yyyy')
    }
    const time = as_dt.toFormat(timeFormat)
    return `${date_part}, ${time}`
}

export function format_money(money: number | string | undefined, currency: string | undefined | null, locale = 'nl') {
    if (!currency && money) {
        // Intl formats any numeric string; its type only admits template-literal numbers.
        const formatter: {format(value: number | string): string} = number_format(locale, {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
            style: 'decimal',
        })
        return formatter.format(money)
    }
    if (currency && (money || money === 0)) {
        const formatter: {format(value: number | string): string} = number_format(locale, {
            currency: currency,
            currencyDisplay: 'code',
            style: 'currency',
        })
        return formatter.format(money)
    }
    return ''
}

// Money arrives as a decimal string as often as a number. A string goes to Intl as it is: Intl rounds
// the decimal it spells, where a Number would first round it to the nearest double.
export function format_money_responsive(value: number | string, short: boolean | null = null, currency = '') {
    let minimum_fraction_digits = 2
    let maximum_fraction_digits = 2

    let formatted_value: number | string = value
    if (short && Number(value) > 100) {
        formatted_value = Number(value) / 1000
        minimum_fraction_digits = 1
        maximum_fraction_digits = 1
    }

    // Intl formats any numeric string; its type only admits template-literal numbers.
    const formatter: {format(value: number | string): string} = number_format($s.language, {
        maximumFractionDigits: maximum_fraction_digits,
        minimumFractionDigits: minimum_fraction_digits,
        style: 'decimal',
    })

    let notation = formatter.format(formatted_value)
    if (currency) {
        notation = `${CURRENCY_SYMBOLS[currency] ?? currency} ${notation}`
    }

    if (short && Number(value) > 100) {
        notation = `${notation}K`
    }

    return notation
}

export function format_percentage(value: number | string, locale = 'en'): string {
    const formatter = number_format(locale, {
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
        style: 'percent',
    })
    return formatter.format(Number(value))
}

export function format_textual_date(date: string | number | Date) {
    const month_names = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ]

    if (!(date instanceof Date)) {
        date = new Date(date)
    }

    let day = date.getDate() as number | string
    if (day === 1) {
        day = '1st'
    } else if (day === 2) {
        day = '2nd'
    } else if (day === 3) {
        day = '3rd'
    } else {
        day += 'th'
    }

    // An invalid date has no month.
    const month = month_names[date.getMonth()] ?? ''
    return `${day} of ${month} ${date.getFullYear()}`
}

export function format_time_to_go(timestamp: string | number | Date) {
    const today = new Date()
    const expiry = new Date(timestamp)
    const diff = Math.floor((expiry.getTime() - today.getTime()) / 1000)
    const seconds = diff % 60
    const diff_minutes = Math.floor(diff / 60)
    const minutes = diff_minutes % 60
    const hours = Math.floor(diff_minutes / 60)

    if (diff > 0) {
        return `${format_leading_zero(hours)}:${format_leading_zero(minutes)}:${format_leading_zero(seconds)}`
    }

    return '00:00:00'
}

/**
 * Capitalize the first letter of all words in a string.
 */
export function titleize(str: string | null): string {
    if (!str) {
        return ''
    }
    return str.replaceAll(/\b[a-z]/g, (letter) => letter.toUpperCase())
}
